import { useEffect, useRef, useState } from 'react';
import { defaultReportRange, validateDateRange } from '../utils/dateRange';

export default function ReportExportBar({ onExport, className = '' }) {
  const [activeExport, setActiveExport] = useState(null);
  const [periodType, setPeriodType] = useState('weekly');
  const [from, setFrom] = useState(defaultReportRange('weekly').from);
  const [to, setTo] = useState(defaultReportRange('weekly').to);
  const [message, setMessage] = useState('');
  const panelRef = useRef(null);

  useEffect(() => {
    const range = defaultReportRange(periodType);
    setFrom(range.from);
    setTo(range.to);
  }, [periodType]);

  useEffect(() => {
    if (!activeExport) return undefined;

    const onPointerDown = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      setActiveExport(null);
      setMessage('');
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [activeExport]);

  const openPanel = (type) => {
    setActiveExport(type);
    setMessage('');
    const range = defaultReportRange(periodType);
    setFrom(range.from);
    setTo(range.to);
  };

  const runExport = () => {
    const rangeError = validateDateRange(from, to);
    if (rangeError) {
      setMessage(rangeError);
      return;
    }

    const result = onExport({
      format: activeExport,
      periodType,
      from,
      to,
    });

    if (typeof result === 'string') {
      setMessage(result);
      return;
    }

    setActiveExport(null);
    setMessage('');
  };

  return (
    <div
      ref={panelRef}
      className={`relative flex flex-wrap items-end justify-end gap-3 mt-10 pt-6 border-t border-lumora-secondary/60 ${className}`}
    >
      <button type="button" className="btn-secondary" onClick={() => openPanel('pdf')}>
        Generate PDF
      </button>
      <button type="button" className="btn-primary" onClick={() => openPanel('excel')}>
        Generate Excel Sheet
      </button>

      {activeExport && (
        <div className="absolute bottom-full right-0 mb-3 w-[min(100vw-2rem,320px)] glass-card shadow-soft p-4 space-y-4 z-20">
          <div>
            <p className="text-sm font-medium text-lumora-dark-wood">
              {activeExport === 'pdf' ? 'Generate PDF' : 'Generate Excel Sheet'}
            </p>
            <p className="text-xs text-lumora-text/50 mt-1">Choose report type and date range</p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-lumora-text/50 mb-1.5">
              Report period
            </label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className="input"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-lumora-text/50 mb-1.5">
                From
              </label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-lumora-text/50 mb-1.5">
                To
              </label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input" />
            </div>
          </div>

          {message && <p className="text-xs text-red-600">{message}</p>}

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => {
                setActiveExport(null);
                setMessage('');
              }}
            >
              Cancel
            </button>
            <button type="button" className="btn-primary text-xs" onClick={runExport}>
              {activeExport === 'pdf' ? 'Download PDF' : 'Download Excel'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
